import React, { memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Text, useTheme } from '@Sage/hoi-poi-ui';

import HtmlPreviewFrame from 'containers/components/HtmlPreviewFrame';
import { getLiteral } from 'utils/getLiteral';

import { buildCampaignDetailsMetaParts, formatFromLine } from './campaignDetailsMeta';

const PREVIEW_TITLE = 'Campaign HTML preview';

const CampaignDetailsHeader = memo(({ campaign }) => {
    const theme = useTheme();
    const labelMuted = theme.colors.textLight.secondary;

    const html = typeof campaign?.body === 'string' ? campaign.body : '';
    const hasBody = Boolean(html?.trim?.());
    const name = typeof campaign?.name === 'string' ? campaign.name : '';
    const subject = typeof campaign?.subject === 'string' ? campaign.subject : '';
    const metaParts = useMemo(() => buildCampaignDetailsMetaParts(campaign ?? {}), [campaign]);
    const fromLine = useMemo(() => formatFromLine(campaign ?? {}), [campaign]);

    const fieldColumns = useMemo(
        () => [
            { fieldKey: 'subject', label: getLiteral('label_subject'), value: subject },
            { fieldKey: 'from', label: getLiteral('label_from'), value: fromLine },
        ],
        [subject, fromLine],
    );

    return (
        <header className="fm-campaign-details__header">
            <div className="fm-campaign-details__header-top">
                <div className="fm-campaign-details__thumb-wrap">
                    {hasBody ? (
                        <div className="fm-campaign-details__thumb-surface">
                            <HtmlPreviewFrame
                                className="fm-campaign-details__thumb-iframe"
                                html={html}
                                title={PREVIEW_TITLE}
                                scale={0.32}
                            />
                        </div>
                    ) : (
                        <div
                            className="fm-campaign-details__thumb-placeholder"
                            aria-hidden={true}
                        />
                    )}
                </div>
                <div className="fm-campaign-details__header-text">
                    <div className="fm-campaign-details__header-lead">
                        {name ? (
                            <Text type="h6" bold className="fm-campaign-details__title">
                                {name}
                            </Text>
                        ) : null}
                        {metaParts.idPart || metaParts.detail ? (
                            <div className="fm-campaign-details__meta-line">
                                {metaParts.idPart ? (
                                    <Text type="subtitle" bold>
                                        {metaParts.idPart}
                                    </Text>
                                ) : null}
                                {metaParts.idPart && metaParts.detail ? (
                                    <span className="fm-campaign-details__meta-line-separator">
                                        •
                                    </span>
                                ) : null}
                                {metaParts.detail ? (
                                    <Text type="caption" color={labelMuted}>
                                        {metaParts.detail}
                                    </Text>
                                ) : null}
                            </div>
                        ) : null}
                    </div>
                    <div className="fm-campaign-details__fields">
                        {fieldColumns.map(({ fieldKey, label, value }) => (
                            <div key={fieldKey} className="fm-campaign-details__field">
                                <Text
                                    className="fm-campaign-details__field-label"
                                    size="small"
                                    color={labelMuted}
                                >
                                    {label}
                                </Text>
                                <Text className="fm-campaign-details__field-value" size="small">
                                    {value || '—'}
                                </Text>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </header>
    );
});

CampaignDetailsHeader.displayName = 'CampaignDetailsHeader';

CampaignDetailsHeader.propTypes = {
    campaign: PropTypes.object,
};

export default CampaignDetailsHeader;
